/**
 * AbilityCatalog — second read view (U2.3) + the first creator entry (U2.4).
 *
 * Mirrors RaceCatalog's master/detail; the right panel swaps to the
 * AbilityCreator when "+ New" is clicked. Tier-aware rendering is
 * server-side. Reuses the shared catalog layout classes (named `.race-*`
 * for now; generalize when a third catalog lands).
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { fetchAbilities, type AbilityDTO } from "../api.js";
import { AbilityCreator } from "./AbilityCreator.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; abilities: AbilityDTO[] };

export function AbilityCatalog() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback((selectId?: string) => {
    setState({ kind: "loading" });
    fetchAbilities()
      .then((abilities) => {
        setState({ kind: "ready", abilities });
        setSelectedId((prev) => selectId ?? prev ?? abilities[0]?.id ?? null);
      })
      .catch((err: Error) => setState({ kind: "error", message: err.message }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(() => {
    if (state.kind !== "ready") return null;
    return state.abilities.find((a) => a.id === selectedId) ?? null;
  }, [state, selectedId]);

  if (state.kind === "loading") {
    return <div className="placeholder">Loading abilities…</div>;
  }
  if (state.kind === "error") {
    return (
      <div className="placeholder" style={{ color: "#ef4444" }}>
        <strong>Failed to load abilities:</strong> {state.message}
      </div>
    );
  }

  const { abilities } = state;

  return (
    <div className="race-catalog">
      <aside className="race-list">
        <div className="race-list-header">
          <h2>Abilities</h2>
          <div className="header-actions">
            <span className="count">{abilities.length}</span>
            <button className="new-btn" onClick={() => setCreating(true)}>
              + New
            </button>
          </div>
        </div>
        {abilities.length === 0 ? (
          <p className="list-empty">None yet. Create the first one.</p>
        ) : (
          <ul>
            {abilities.map((ability) => (
              <li key={ability.id}>
                <button
                  className={`race-list-item ${
                    ability.id === selectedId && !creating ? "active" : ""
                  }`}
                  onClick={() => {
                    setCreating(false);
                    setSelectedId(ability.id);
                  }}
                >
                  <span className="name">{ability.name}</span>
                  <span className="lifespan">{ability.systemType}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>
      <section className="race-detail">
        {creating ? (
          <AbilityCreator
            onCreated={(ability) => {
              setCreating(false);
              load(ability.id);
            }}
            onCancel={() => setCreating(false)}
          />
        ) : selected ? (
          <AbilityDetail ability={selected} />
        ) : (
          <div className="placeholder">Pick an ability, or create one.</div>
        )}
      </section>
    </div>
  );
}

function AbilityDetail({ ability }: { ability: AbilityDTO }) {
  return (
    <article>
      <header className="detail-header">
        <h2>{ability.name}</h2>
        <span className="category">{ability.systemType}</span>
      </header>

      <dl className="detail-grid">
        <dt>Race access</dt>
        <dd>
          {ability.raceCompatibility.length > 0 ? (
            <span className="muted">{ability.raceCompatibility.length} races</span>
          ) : (
            <span className="muted">Unrestricted</span>
          )}
        </dd>

        <dt>Restrictions</dt>
        <dd>
          {ability.restrictions.length > 0 ? (
            <ul className="inline-list">
              {ability.restrictions.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : (
            <span className="empty">—</span>
          )}
        </dd>

        <dt>Prerequisites</dt>
        <dd>
          {ability.prerequisites.length > 0 ? (
            <ul className="inline-list">
              {ability.prerequisites.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          ) : (
            <span className="empty">—</span>
          )}
        </dd>

        <dt>Tiers</dt>
        <dd>
          {ability.tiers.length > 0 ? (
            <span className="muted">{ability.tiers.length} defined</span>
          ) : (
            <span className="empty">—</span>
          )}
        </dd>
      </dl>

      {ability.description.length > 0 && (
        <section className="description">
          <h3>Description</h3>
          {ability.description.map((entry, i) => (
            <p key={i} className={`tiered tier-${entry.tier}`}>
              {entry.tier === "tier_2" && <span className="tier-badge">tier 2</span>}
              {entry.text}
            </p>
          ))}
        </section>
      )}

      {ability.weaknesses.length > 0 && (
        <section className="weaknesses">
          <h3>Weaknesses</h3>
          {ability.weaknesses.map((entry, i) => (
            <p key={i} className={`tiered tier-${entry.tier}`}>
              {entry.tier === "tier_2" && <span className="tier-badge">tier 2</span>}
              {entry.text}
            </p>
          ))}
        </section>
      )}
    </article>
  );
}
